import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import type { ILightingClassConfig, IRoadLayoutConfig } from "types";
import { roundToTwo } from "utils/helpers";
import { useLuminanceData } from "../../hooks/useLuminanceData";
import { getMRoadClassTargets } from "../../utils/road-class";
import LuminanceHeatmap from "../heatmaps/LuminanceHeatmap";
import { RoadState } from "./RoadState";
import getStyles from "./styles";
import CriteriaTable from "./Criteria";

interface IProps {
  luminance: any;
  luminance_summary: any;
  illuminance: any;
  surfaces: any;
  sidewalk_placing: any;
  lightingClassConfig: ILightingClassConfig;
  divId: string;
  displayHeader: boolean;
  ulor: number;
  s_state: string;
  e_state_left: string;
  e_state_right: string;
  roadLayoutConfig: IRoadLayoutConfig;
  index: number;
  showLane: boolean;
}

const LuminanceTable: FC<IProps> = ({
  luminance,
  luminance_summary,
  illuminance,
  surfaces,
  sidewalk_placing,
  lightingClassConfig,
  divId,
  displayHeader,
  ulor,
  e_state_left,
  e_state_right,
  roadLayoutConfig,
  s_state,
  index,
  showLane,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).LuminanceTable, [theme]);

  const { ptsX, ptsY, dimX, dimY } = useLuminanceData({ luminance, illuminance, surfaces, sidewalk_placing });
  const { targetValue, uo_target, ul_target, fti_target, rei_target } = getMRoadClassTargets(lightingClassConfig);

  const roadClass = lightingClassConfig.roadClasses;
  const customRoadClass = lightingClassConfig.customClasses?.mainCustomClass;

  return (
    <Stack sx={styles.container}>
      {displayHeader && (
        <Box>
          <Typography variant="h2" sx={styles.title}>
            {t("Luminance calculation field:")} <span id="dimX0">{dimX}</span> m &times; <span id="dimY0">{roundToTwo(dimY)}</span> m (
            <span id="ptsX0">{ptsX}</span> &times; <span id="ptsY0">{ptsY}</span> {t("pts")})
          </Typography>

          <TableContainer component={Paper} sx={styles.card}>
            <Table padding="none" sx={styles.table}>
              <TableHead sx={styles.thead}>
                <TableRow>
                  <TableCell>
                    {t("Roadway")} {customRoadClass?.class_type ? `${customRoadClass.class_type} ${t("Customized")}` : roadClass}:
                  </TableCell>
                  <TableCell>
                    L̄<sub>m</sub>
                  </TableCell>
                  <TableCell>
                    U<sub>o</sub>
                  </TableCell>
                  <TableCell>
                    U<sub>d</sub>
                  </TableCell>
                  <TableCell>
                    f<sub>TI</sub>
                  </TableCell>
                  <TableCell>
                    R<sub>EI</sub>
                  </TableCell>
                  <TableCell>
                    R<sub>UL</sub>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={styles.tbody}>
                <TableRow>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell>
                    {roundToTwo(luminance_summary.l_avg)} cd/m<sup>2</sup>
                  </TableCell>
                  <TableCell>{roundToTwo(luminance_summary.u_o)}</TableCell>
                  <TableCell>{roundToTwo(luminance_summary.u_l)}</TableCell>
                  <TableCell>{roundToTwo(luminance_summary.f_ti)}%</TableCell>
                  <TableCell>{luminance.rei === -1.0 ? "-" : roundToTwo(luminance.rei)}</TableCell>
                  <TableCell>{Math.round(Number(ulor))}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("Target Value")}</TableCell>
                  <TableCell>
                    {targetValue} cd/m<sup>2</sup>
                  </TableCell>
                  <TableCell>{uo_target}</TableCell>
                  <TableCell>{ul_target}</TableCell>
                  <TableCell>{fti_target}</TableCell>
                  <TableCell>{rei_target}</TableCell>
                  <TableCell>{Math.round(Number(ulor))}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Met/Not Met Status */}
            <CriteriaTable
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              s_state={s_state}
              e_state_left={e_state_left}
              e_state_right={e_state_right}
            />
          </TableContainer>
        </Box>
      )}

      {/* @todo: Only for printing, remove once we move to Carbone */}
      {displayHeader && (
        <RoadState
          s_state={s_state}
          e_state_left={e_state_left}
          e_state_right={e_state_right}
          roadLayoutConfig={roadLayoutConfig}
          lightingClassConfig={lightingClassConfig}
        />
      )}

      <LuminanceHeatmap
        id={divId}
        index={index}
        showLane={showLane}
        luminance={luminance}
        illuminance={illuminance}
        surfaces={surfaces}
        sidewalkPlacing={sidewalk_placing}
        ulor={ulor}
        roadClass={roadClass}
        customRoadClass={customRoadClass}
        lightingClassConfig={lightingClassConfig}
      />
    </Stack>
  );
};

export default LuminanceTable;
